const emit = (target, type, detail) => target.dispatchEvent(new CustomEvent(type, { detail }));

// https://developers.soundcloud.com/docs/api/html5-widget#playground
export default class SoundcloudPlayer extends EventTarget {
	static #getSCPromise = null;
	#getWidgetPromise = null;
	#iframe = null;
	#endSeconds = 0;

	constructor(elemId) {
		super();
		this.elemId = elemId;
	}

	static getName() { return 'SoundCloud'; }
	static urlMatches(url) {
		return /^https?:\/\/(www\.)?soundcloud\.com\/[^/]+\/[^/]+$/.test(url);
	}

	static getSC() {
		return SoundcloudPlayer.#getSCPromise ||= new Promise((resolve, reject) => {
			const script = document.createElement('script');
			const cleanup = () => { script.onerror = null; script.onload = null; };
			const onError = (err) => { cleanup(); reject(err); };
			const onLoad = () => { cleanup(); resolve(window.SC); };
			script.onerror = onError;
			script.onload = onLoad;
			script.src = 'https://w.soundcloud.com/player/api.js';
			document.head.appendChild(script);
		});
	}

	getWidget(url) {
		if(!url && !this.#getWidgetPromise)
			return Promise.reject(new ReferenceError('No URL specified'));

		return this.#getWidgetPromise ||= new Promise((resolve, reject) => {
			SoundcloudPlayer.getSC().then(SC => {
				const container = document.getElementById(this.elemId);
				this.#iframe = document.createElement('iframe');
				this.#iframe.id = `${this.elemId}-iframe`;
				this.#iframe.allow = 'autoplay';
				this.#iframe.width = '100%';
				this.#iframe.height = '100%';
				this.#iframe.frameBorder = 'no';
				this.#iframe.src = `https://w.soundcloud.com/player/?${new URLSearchParams({ url, show_artwork: true })}`;
				container.appendChild(this.#iframe);

				const widget = SC.Widget(this.#iframe);
				widget.bind(SC.Widget.Events.ERROR, err => {
					widget.unbind(SC.Widget.Events.ERROR);
					widget.unbind(SC.Widget.Events.READY);
					reject(err);
				});
				widget.bind(SC.Widget.Events.READY, () => {
					widget.unbind(SC.Widget.Events.READY);
					widget.unbind(SC.Widget.Events.ERROR);
					widget.bind(SC.Widget.Events.FINISH, () => emit(this, 'status', { status: 'finished' }));
					widget.bind(SC.Widget.Events.ERROR, ev => emit(this, 'error', ev));
					widget.bind(SC.Widget.Events.PLAY_PROGRESS, ev => {
						emit(this, 'progress', ev);
						if(this.#endSeconds > 0 && ev.currentPosition >= this.#endSeconds * 1000) {
							widget.pause();
							emit(this, 'status', { status: 'finished' });
						}
					});
					widget.bind(SC.Widget.Events.PLAY,  () => emit(this, 'status', { status: 'playing' }));
					widget.bind(SC.Widget.Events.PAUSE, () => emit(this, 'status', { status: 'paused' }));
					resolve(widget);
				});
			}).catch(reject);
		});
	}

	async load(url, startTime, maxDuration) {
		document.getElementById(this.elemId).style.display = 'block';
		const isReload = !!this.#getWidgetPromise;
		const widget = await this.getWidget(url);
		if(isReload)
			widget.load(url, { auto_play: true });
		if(startTime)
			widget.seekTo(startTime * 1000);
		this.#endSeconds = (startTime || 0) + (maxDuration || 0);
	}

	async play() {
		const widget = await this.getWidget();
		widget.play();
	}

	async pause() {
		const widget = await this.getWidget();
		widget.pause();
	}

	async togglePause() {
		const widget = await this.getWidget();
		widget.toggle();
	}

	async stop() {
		const widget = await this.getWidget();
		widget.pause();
		widget.seekTo(0);
	}

	async getCurrentTime() {
		const widget = await this.getWidget();
		return new Promise(resolve => widget.getPosition(resolve));
	}

	async setVolume(vol) {
		const widget = await this.getWidget();
		widget.setVolume(vol);
	}

	async cleanup() {
		try {
			this.#iframe?.parentNode?.removeChild(this.#iframe);
		} catch(e) {
			// ignore
		} finally {
			this.#getWidgetPromise = null;
			this.#iframe = null;
		}
	}
}
